import { genericMemo } from "@redotech/react-util/component";
import { useSearch } from "@redotech/react-util/search";
import { TextMatchExpression } from "@redotech/redo-flows-core-schema-common/expressions/text-expression";
import {
  isFieldOfType,
  Schema,
} from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { useTranslation } from "@redotech/redo-i18n";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { DeepPartial, DeepPartialExcept } from "@redotech/util/type";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

export const EnumInspector = genericMemo(function EnumInspector({
  selectedField,
  expression,
  setExpression,
  showValidationErrors,
}: {
  selectedField: Schema["fields"][string];
  expression: DeepPartialExcept<TextMatchExpression, "field">;
  setExpression: (expression: DeepPartial<TextMatchExpression>) => void;
  showValidationErrors?: boolean;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "text_match", operator: "equals" });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const allOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!isFieldOfType(selectedField, "Enum")) {
      return [];
    }
    return (selectedField.values ?? [])
      .map((value) => ({
        value,
        text: selectedField.prettyValues?.[value] ?? value,
      }))
      .toSorted((a, b) => a.text.localeCompare(b.text))
      .map(({ value, text }) => ({
        id: value,
        value,
        type: "badge" as const,
        badge: { text },
      }));
  }, [selectedField]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(
    () =>
      allOptions.filter((option) =>
        expression.matchValues?.includes(option.value),
      ),
    [allOptions, expression.matchValues],
  );

  /** Searches the label on screen; the raw value behind it is often unreadable. */
  const searcher = useMemo(
    () =>
      new Fuse<BadgeRedoListItem<string>>([], {
        keys: ["badge.text"],
        threshold: 0.3,
      }),
    [],
  );

  const [searchText, setSearchText] = useState<string>("");
  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(!expression.matchValues?.length);

  const searchResults = useSearch(searcher, allOptions, searchText);

  const errorText = useMemo(() => {
    if (
      (!expression.matchValues || expression.matchValues.length === 0) &&
      touched
    ) {
      return t("At least one value is required");
    }
    return undefined;
  }, [expression.matchValues, touched, t]);

  return (
    <RedoTagsDropdownInput<string>
      autoFocus={delayedAutoFocus}
      description={errorText}
      key={delayedAutoFocus.toString()}
      label={t("Value")}
      onBlur={() => setTouched(true)}
      options={searchResults}
      searchString={searchText}
      selectedOptions={selectedOptions}
      setSearchString={setSearchText}
      setSelectedOptions={(selectedOptions) => {
        setExpression({
          ...expression,
          matchValues: selectedOptions.map((option) => option.value),
        });
      }}
      size="xs"
      state={errorText ? "error" : undefined}
    />
  );
});
